// Lecteurs audio du Blind Test, partagés par ses écrans.
//
// Un iPhone ne laisse un élément <audio> jouer seul qu'après l'avoir lancé
// une première fois pendant un geste du joueur. La musique démarre sur un
// signal du serveur, sans geste : on garde donc quelques lecteurs, débloqués
// au premier toucher dans l'application (rejoindre une partie suffit), et les
// écrans de jeu s'en servent au lieu de créer les leurs.

const POOL_SIZE = 5;   // la Cacophonie joue jusqu'à 5 morceaux à la fois
// 25 ms de silence, de quoi lancer chaque lecteur une fois.
const SILENCE = "data:audio/wav;base64,UklGRuwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YcgAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==";

const pool = typeof Audio === "undefined" ? [] : Array.from({ length: POOL_SIZE }, () => {
  const a = new Audio();
  a.preload = "auto";
  return a;
});
let unlocked = false;
let ctx = null;

function unlock() {
  if (unlocked) return;
  unlocked = true;
  // Sur iOS 17+, la sortie Web Audio (utilisée pour le fondu) ignore alors
  // l'interrupteur silencieux, comme un <audio> ordinaire.
  try { if (navigator.audioSession) navigator.audioSession.type = "playback"; } catch (_) {}
  const AC = window.AudioContext || window.webkitAudioContext;
  if (AC) { try { ctx = new AC(); ctx.resume(); } catch (_) { ctx = null; } }
  for (const a of pool) {
    if (a.src) continue;           // déjà occupé par une partie
    a.src = SILENCE;
    a.play().then(() => a.pause()).catch(() => {});
  }
  ["pointerdown", "keydown", "touchend"].forEach(e => window.removeEventListener(e, unlock, true));
}
if (typeof window !== "undefined") {
  ["pointerdown", "keydown", "touchend"].forEach(e => window.addEventListener(e, unlock, true));
}

export const btPlayer = (i = 0) => pool[i];

// Charge `url` dans le lecteur seulement s'il ne la joue pas déjà : une
// reprise après un buzz ne doit pas recharger le fichier.
export function load(audio, url) {
  if (audio.dataset.url !== url) {
    audio.dataset.url = url;
    audio.src = url;
  }
  resetLevel(audio);
}

// iOS ignore `volume` : le fondu y passe par un gain Web Audio, branché au
// premier fondu (fichiers du serveur seulement, une URL externe sans CORS
// sortirait muette).
const volumeWorks = (() => {
  if (typeof Audio === "undefined") return false;
  const a = new Audio(); a.volume = 0.5; return a.volume === 0.5;
})();

function gainOf(audio) {
  if (volumeWorks || !ctx) return null;
  if (!audio._pqGain) {
    try {
      if (new URL(audio.src, window.location.href).origin !== window.location.origin) return null;
      const src = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      src.connect(gain).connect(ctx.destination);
      audio._pqGain = gain;
    } catch (_) { return null; }
  }
  return audio._pqGain;
}

function resetLevel(audio) {
  clearInterval(audio._pqFade);
  audio.volume = 1;
  if (audio._pqGain) audio._pqGain.gain.value = 1;
}

// Baisse le son jusqu'au silence en `ms`, puis met en pause.
export function fadeOut(audio, ms = 1500) {
  if (!audio || audio.paused) return;
  clearInterval(audio._pqFade);
  const gain = gainOf(audio);
  if (gain) {
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + ms / 1000);
    audio._pqFade = setTimeout(() => audio.pause(), ms);
    return;
  }
  const from = audio.volume, start = Date.now();
  audio._pqFade = setInterval(() => {
    const k = Math.min(1, (Date.now() - start) / ms);
    audio.volume = from * (1 - k);
    if (k >= 1) { clearInterval(audio._pqFade); audio.pause(); }
  }, 50);
}

// Arrêt net (interruption, sortie de l'écran).
export function stop(audio) {
  if (!audio) return;
  clearInterval(audio._pqFade); clearTimeout(audio._pqFade);
  audio.pause();
}
